import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { Autocomplete, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { useListIcons } from '~/hooks/apis/accommodation/useListIcons';

import { BeddingConfiguration } from '~/types/roomPolicy';

interface RoomPolicyBeddingEditorProps {
  beddingConfiguration: BeddingConfiguration;
  beddingExtras: Array<BeddingConfiguration>;
  onBeddingConfigurationChange: (value: BeddingConfiguration) => void;
  onBeddingExtrasChange: (items: Array<BeddingConfiguration>) => void;
}

export default function RoomPolicyBeddingEditor({
  beddingConfiguration,
  beddingExtras,
  onBeddingConfigurationChange,
  onBeddingExtrasChange,
}: RoomPolicyBeddingEditorProps) {
  const { data: icons = [], isLoading: iconsLoading } = useListIcons();

  const handleExtraTextChange = (index: number, value: string) => {
    const updated = [...beddingExtras];
    updated[index] = { ...updated[index], bedding: value };
    onBeddingExtrasChange(updated);
  };

  const handleExtraIconChange = (index: number, newValue: string | null) => {
    const updated = [...beddingExtras];
    updated[index] = { ...updated[index], icon: newValue ?? '' };
    onBeddingExtrasChange(updated);
  };

  const handleAddExtra = () => {
    onBeddingExtrasChange([...beddingExtras, { bedding: '', icon: '' }]);
  };

  const handleRemoveExtra = (index: number) => {
    onBeddingExtrasChange(beddingExtras.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Bedding configuration
      </Typography>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            value={beddingConfiguration.bedding || 'Not filled'}
            onChange={(e) => onBeddingConfigurationChange({ ...beddingConfiguration, bedding: e.target.value })}
            disabled
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={icons}
            loading={iconsLoading}
            value={beddingConfiguration.icon || null}
            onChange={(_event, newValue) =>
              onBeddingConfigurationChange({ ...beddingConfiguration, icon: newValue ?? '' })
            }
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => <TextField {...params} label="Icon" placeholder="Select icon" />}
          />
          <Chip
            icon={<LockIcon fontSize="small" />}
            label="Room default"
            size="small"
            color="default"
            variant="outlined"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <Tooltip title="This is the room default and cannot be removed">
            <span>
              <IconButton size="small" disabled>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {beddingExtras.map((extra, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              value={extra.bedding}
              onChange={(e) => handleExtraTextChange(index, e.target.value)}
            />
            <Autocomplete
              size="small"
              sx={{ minWidth: 250 }}
              options={icons}
              loading={iconsLoading}
              value={extra.icon || null}
              onChange={(_event, newValue) => handleExtraIconChange(index, newValue)}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => <TextField {...params} label="Icon" placeholder="Select icon" />}
            />
            <IconButton size="small" onClick={() => handleRemoveExtra(index)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAddExtra} sx={{ mt: 1 }}>
        Add item
      </Button>
    </Box>
  );
}
